import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    Collapse,
    Pagination,
    Grid,
    Select,
    MenuItem,
    TextField,
    Button,
    FormControl,
    InputLabel,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { supabase } from '../services/supabaseClient';

interface AuditLog {
    id: string;
    table_name: string;
    record_id: string;
    operation: 'INSERT' | 'UPDATE' | 'DELETE';
    old_data: any;
    new_data: any;
    changed_by: string;
    created_at: string;
}

interface UserMap {
    [key: string]: string;
}

interface ObraMap {
    [key: string]: string;
}

interface ReferenceMap {
    [key: string]: string;
}

const TABLE_LABELS: Record<string, string> = {
    obras: 'Obras',
    lancamentos_financeiros: 'Lançamentos Financeiros',
    lancamento_itens: 'Itens de Lançamento',
    fornecedores: 'Fornecedores',
    investidores: 'Investidores',
    anexos: 'Anexos de Lançamento',
    categorias: 'Categorias',
    fases_da_obra: 'Fases da Obra',
    catalogo_itens: 'Catálogo de Itens',
    catalogo_itens_fases: 'Vínculo Itens × Fases',
    aportes_resgates: 'Aportes e Resgates',
    aportes_resgates_anexos: 'Anexos de Aportes/Resgates',
    orcamentos_categorias: 'Orçamentos por Categoria',
    cronogramas: 'Cronogramas',
    contratos: 'Contratos',
    obra_documentos: 'Documentos da Obra',
    obra_usuarios: 'Usuários da Obra',
    assinaturas_digitais: 'Assinaturas Digitais',
    unidades: 'Unidades',
    perfis: 'Perfis de Acesso',
    configuracoes_sistema: 'Configurações / Chaves API',
    audit_logs: 'Logs de Auditoria',
};

const COMMON_TABLES = Object.keys(TABLE_LABELS);

const formatKey = (key: string) => {
    return key
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const resolveValue = (key: string, val: any, referenceMap: ReferenceMap, obraMap: ObraMap) => {
    if (val === null || val === undefined || val === '') return '-';
    if (key === 'obra_id') return `${val} - ${obraMap[val] || 'Desconhecido'}`;
    if (key === 'fornecedor_id') return `${val} - ${referenceMap['fornecedor_' + val] || 'Desconhecido'}`;
    if (key === 'investidor_id') return `${val} - ${referenceMap['investidor_' + val] || 'Desconhecido'}`;
    if (key === 'categoria_id') return `${val} - ${referenceMap['categoria_' + val] || 'Desconhecido'}`;
    if (key === 'fase_id') return `${val} - ${referenceMap['fase_' + val] || 'Desconhecido'}`;
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
};

const LogDetails: React.FC<{ row: AuditLog; referenceMap: ReferenceMap; obraMap: ObraMap }> = ({
    row,
    referenceMap,
    obraMap,
}) => {
    const allKeys = new Set<string>();
    if (row.old_data) Object.keys(row.old_data).forEach((k) => allKeys.add(k));
    if (row.new_data) Object.keys(row.new_data).forEach((k) => allKeys.add(k));
    const keysArray = Array.from(allKeys).sort();

    return (
        <Table
            size="small"
            aria-label="details"
            sx={{ border: '1px solid', borderColor: 'divider', mb: 2, bgcolor: 'background.paper' }}
        >
            <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Campo</TableCell>
                    {row.operation !== 'INSERT' && <TableCell sx={{ fontWeight: 'bold' }}>Valor Antigo</TableCell>}
                    {row.operation !== 'DELETE' && <TableCell sx={{ fontWeight: 'bold' }}>Valor Novo</TableCell>}
                </TableRow>
            </TableHead>
            <TableBody>
                {keysArray.map((key) => {
                    const oldValRaw = row.old_data ? row.old_data[key] : undefined;
                    const newValRaw = row.new_data ? row.new_data[key] : undefined;

                    const oldVal = resolveValue(key, oldValRaw, referenceMap, obraMap);
                    const newVal = resolveValue(key, newValRaw, referenceMap, obraMap);

                    const isChanged = row.operation === 'UPDATE' && String(oldValRaw) !== String(newValRaw);

                    return (
                        <TableRow
                            key={key}
                            sx={{
                                backgroundColor: isChanged ? 'rgba(255, 152, 0, 0.12)' : 'inherit',
                            }}
                        >
                            <TableCell>{formatKey(key)}</TableCell>
                            {row.operation !== 'INSERT' && (
                                <TableCell
                                    sx={{
                                        color: isChanged ? 'error.main' : 'text.primary',
                                        textDecoration: isChanged ? 'line-through' : 'none',
                                    }}
                                >
                                    {oldVal}
                                </TableCell>
                            )}
                            {row.operation !== 'DELETE' && (
                                <TableCell
                                    sx={{
                                        color: isChanged ? 'success.main' : 'text.primary',
                                        fontWeight: isChanged ? 'bold' : 'normal',
                                    }}
                                >
                                    {newVal}
                                </TableCell>
                            )}
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
};

const LancamentoItemsList: React.FC<{ lancamentoId: string }> = ({ lancamentoId }) => {
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchItems = async () => {
            setLoading(true);
            const { data, error: err } = await supabase
                .from('lancamento_itens')
                .select('*')
                .eq('lancamento_id', lancamentoId);

            if (err) setError(err.message);
            else setItems(data || []);
            setLoading(false);
        };
        fetchItems();
    }, [lancamentoId]);

    if (loading)
        return (
            <Box sx={{ mt: 2 }}>
                <CircularProgress size={20} />
            </Box>
        );
    if (error)
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                {error}
            </Alert>
        );
    if (items.length === 0)
        return (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Nenhum item encontrado (estado atual) para este lançamento.
            </Typography>
        );

    const total = items.reduce((acc, item) => {
        const sub = (Number(item.quantidade) || 0) * (Number(item.valor_unitario) || 0);
        const desc = Number(item.desconto_valor) || 0;
        return acc + (sub - desc);
    }, 0);

    return (
        <Box
            sx={{
                mt: 2,
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                Itens deste Lançamento (Estado Atual)
            </Typography>
            <Table size="small" sx={{ bgcolor: 'background.paper' }}>
                <TableHead>
                    <TableRow>
                        <TableCell>Descrição</TableCell>
                        <TableCell align="right">Qtd</TableCell>
                        <TableCell align="right">V. Unitário</TableCell>
                        <TableCell align="right">Desconto</TableCell>
                        <TableCell align="right">Total</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((item) => {
                        const sub = (Number(item.quantidade) || 0) * (Number(item.valor_unitario) || 0);
                        const desc = Number(item.desconto_valor) || 0;
                        const itemTotal = sub - desc;
                        return (
                            <TableRow key={item.id}>
                                <TableCell>{item.descricao || '-'}</TableCell>
                                <TableCell align="right">{Number(item.quantidade).toLocaleString('pt-BR')}</TableCell>
                                <TableCell align="right">
                                    {Number(item.valor_unitario).toLocaleString('pt-BR', {
                                        style: 'currency',
                                        currency: 'BRL',
                                    })}
                                </TableCell>
                                <TableCell align="right">
                                    {desc > 0
                                        ? desc.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                        : '-'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                    {itemTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                    <TableRow>
                        <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold' }}>
                            Valor Total do Lançamento:
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </Box>
    );
};

const Row: React.FC<{
    row: AuditLog;
    userName: string;
    obraName: string;
    referenceMap: ReferenceMap;
    obraMap: ObraMap;
}> = ({ row, userName, obraName, referenceMap, obraMap }) => {
    const [open, setOpen] = useState(false);
    const friendlyTable = TABLE_LABELS[row.table_name] || row.table_name;

    return (
        <>
            <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    {new Date(row.created_at).toLocaleString('pt-BR')}
                </TableCell>
                <TableCell>{userName}</TableCell>
                <TableCell>{obraName}</TableCell>
                <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {friendlyTable}
                    </Typography>
                    {friendlyTable !== row.table_name && (
                        <Typography variant="caption" color="text.secondary">
                            {row.table_name}
                        </Typography>
                    )}
                </TableCell>
                <TableCell>
                    <Chip
                        label={row.operation}
                        color={
                            row.operation === 'INSERT'
                                ? 'success'
                                : row.operation === 'UPDATE'
                                ? 'warning'
                                : 'error'
                        }
                        size="small"
                        sx={{ fontWeight: 600 }}
                    />
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, mt: 2 }}>
                            <Typography
                                variant="h6"
                                gutterBottom
                                component="div"
                                sx={{ fontSize: '0.95rem', fontWeight: 'bold' }}
                            >
                                Detalhes da Operação (ID do Registro: {row.record_id || '-'})
                            </Typography>
                            <LogDetails row={row} referenceMap={referenceMap} obraMap={obraMap} />

                            {row.table_name === 'lancamentos_financeiros' && row.record_id && (
                                <LancamentoItemsList lancamentoId={row.record_id} />
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const AuditLogsPage: React.FC = () => {
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [userMap, setUserMap] = useState<UserMap>({});
    const [obraMap, setObraMap] = useState<ObraMap>({});
    const [referenceMap, setReferenceMap] = useState<ReferenceMap>({});
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [allObras, setAllObras] = useState<any[]>([]);

    // Filtros
    const [filterObra, setFilterObra] = useState<string>('');
    const [filterUser, setFilterUser] = useState<string>('');
    const [filterTable, setFilterTable] = useState<string>('');
    const [filterOperation, setFilterOperation] = useState<string>('');
    const [filterStartDate, setFilterStartDate] = useState<string>('');
    const [filterEndDate, setFilterEndDate] = useState<string>('');

    const ROWS_PER_PAGE = 20;

    const handleFilterChange = (setter: React.Dispatch<React.SetStateAction<string>>) => (event: any) => {
        setter(event.target.value);
        setPage(1);
    };

    const clearFilters = () => {
        setFilterObra('');
        setFilterUser('');
        setFilterTable('');
        setFilterOperation('');
        setFilterStartDate('');
        setFilterEndDate('');
        setPage(1);
    };

    // Carregar usuários e obras apenas uma vez na montagem
    useEffect(() => {
        const fetchStaticMaps = async () => {
            try {
                const [{ data: obrasData }, { data: userData, error: userError }] = await Promise.all([
                    supabase.from('obras').select('id, nome').order('nome'),
                    supabase.functions.invoke('list-users'),
                ]);

                if (obrasData) {
                    setAllObras(obrasData);
                    const initialObraMap: ObraMap = {};
                    obrasData.forEach((o: any) => {
                        initialObraMap[o.id] = o.nome;
                    });
                    setObraMap((prev) => ({ ...initialObraMap, ...prev }));
                }

                if (!userError && userData?.users) {
                    const users: UserMap = {};
                    userData.users.forEach((u: any) => {
                        users[u.id] = u.user_metadata?.name || u.email;
                    });
                    setUserMap(users);
                }
            } catch (err) {
                console.error('Erro ao carregar mapas iniciais de auditoria:', err);
            }
        };
        fetchStaticMaps();
    }, []);

    useEffect(() => {
        const fetchLogs = async () => {
            setLoading(true);
            setError(null);
            try {
                let query = supabase.from('audit_logs').select('*', { count: 'exact' });

                if (filterUser) query = query.eq('changed_by', filterUser);
                if (filterTable) query = query.eq('table_name', filterTable);
                if (filterOperation) query = query.eq('operation', filterOperation);
                if (filterStartDate) query = query.gte('created_at', `${filterStartDate}T00:00:00Z`);
                if (filterEndDate) query = query.lte('created_at', `${filterEndDate}T23:59:59.999Z`);

                if (filterObra) {
                    query = query.or(
                        `and(table_name.eq.obras,record_id.eq.${filterObra}),new_data->>obra_id.eq.${filterObra},old_data->>obra_id.eq.${filterObra}`
                    );
                }

                const { data, error, count } = await query
                    .order('created_at', { ascending: false })
                    .range((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE - 1);

                if (error) throw error;

                const logsData = data || [];
                setLogs(logsData);

                const fornecedorIds = new Set<string>();
                const investidorIds = new Set<string>();
                const categoriaIds = new Set<string>();
                const faseIds = new Set<string>();

                logsData.forEach((log) => {
                    const checkData = (d: any) => {
                        if (!d) return;
                        if (d.fornecedor_id) fornecedorIds.add(d.fornecedor_id.toString());
                        if (d.investidor_id) investidorIds.add(d.investidor_id.toString());
                        if (d.categoria_id) categoriaIds.add(d.categoria_id.toString());
                        if (d.fase_id) faseIds.add(d.fase_id.toString());
                    };
                    checkData(log.new_data);
                    checkData(log.old_data);
                });

                const newRefMap: ReferenceMap = {};

                const fetchRefs = async (table: string, column: string, ids: Set<string>, prefix: string) => {
                    if (ids.size === 0) return;
                    const { data: refData } = await supabase
                        .from(table)
                        .select(`id, ${column}`)
                        .in('id', Array.from(ids));
                    if (refData) {
                        refData.forEach((item: any) => {
                            newRefMap[prefix + '_' + item.id] = item[column];
                        });
                    }
                };

                await Promise.all([
                    fetchRefs('fornecedores', 'nome_razao_social', fornecedorIds, 'fornecedor'),
                    fetchRefs('investidores', 'nome', investidorIds, 'investidor'),
                    fetchRefs('categorias', 'nome', categoriaIds, 'categoria'),
                    fetchRefs('fases_da_obra', 'nome', faseIds, 'fase'),
                ]);

                setReferenceMap(newRefMap);
                setTotalPages(count ? Math.max(1, Math.ceil(count / ROWS_PER_PAGE)) : 1);
            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchLogs();
    }, [page, filterUser, filterTable, filterOperation, filterStartDate, filterEndDate, filterObra]);

    return (
        <Container maxWidth="lg" sx={{ my: 4 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    Logs de Auditoria
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Histórico detalhado de criações, edições e exclusões realizadas no sistema
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Obra</InputLabel>
                            <Select value={filterObra} label="Obra" onChange={handleFilterChange(setFilterObra)}>
                                <MenuItem value="">
                                    <em>Todas</em>
                                </MenuItem>
                                {allObras.map((obra) => (
                                    <MenuItem key={obra.id} value={obra.id}>
                                        {obra.nome}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Usuário</InputLabel>
                            <Select value={filterUser} label="Usuário" onChange={handleFilterChange(setFilterUser)}>
                                <MenuItem value="">
                                    <em>Todos</em>
                                </MenuItem>
                                {Object.entries(userMap).map(([id, name]) => (
                                    <MenuItem key={id} value={id}>
                                        {name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Tabela</InputLabel>
                            <Select value={filterTable} label="Tabela" onChange={handleFilterChange(setFilterTable)}>
                                <MenuItem value="">
                                    <em>Todas</em>
                                </MenuItem>
                                {COMMON_TABLES.map((table) => (
                                    <MenuItem key={table} value={table}>
                                        {TABLE_LABELS[table] || table}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Operação</InputLabel>
                            <Select
                                value={filterOperation}
                                label="Operação"
                                onChange={handleFilterChange(setFilterOperation)}
                            >
                                <MenuItem value="">
                                    <em>Todas</em>
                                </MenuItem>
                                <MenuItem value="INSERT">Criação (INSERT)</MenuItem>
                                <MenuItem value="UPDATE">Edição (UPDATE)</MenuItem>
                                <MenuItem value="DELETE">Exclusão (DELETE)</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Data Início"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={filterStartDate}
                            onChange={handleFilterChange(setFilterStartDate)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Data Fim"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={filterEndDate}
                            onChange={handleFilterChange(setFilterEndDate)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                        <Button fullWidth variant="outlined" onClick={clearFilters}>
                            Limpar Filtros
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                        <Table aria-label="collapsible table">
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    <TableCell sx={{ fontWeight: 700 }}>Data/Hora</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Usuário</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Obra</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Módulo / Tabela</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Operação</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                            <Typography color="text.secondary">
                                                Nenhum registro de auditoria encontrado para os filtros selecionados.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    logs.map((log) => {
                                        const obraId =
                                            log.table_name === 'obras'
                                                ? log.record_id?.toString()
                                                : (log.new_data?.obra_id || log.old_data?.obra_id)?.toString();
                                        const obraName = obraId && obraMap[obraId] ? obraMap[obraId] : 'N/A';

                                        return (
                                            <Row
                                                key={log.id}
                                                row={log}
                                                userName={userMap[log.changed_by] || log.changed_by || 'Sistema'}
                                                obraName={obraName}
                                                referenceMap={referenceMap}
                                                obraMap={obraMap}
                                            />
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, value) => setPage(value)}
                            color="primary"
                        />
                    </Box>
                </>
            )}
        </Container>
    );
};

export default AuditLogsPage;
